// US-087: pure merge logic for folding one Phase-2 (background reorder-suggestion
// report) row into an already-rendered Phase-1 table row.
//
// Never overwrites quantity/cost/total on a row the buyer has already edited
// (row.userEdited) — see PurchaseOrderCreate.vue's updateItemTotal. Every other
// report field (case_qty, stock levels, urgency, ...) always fills in, since those
// are read-only display columns regardless of edit state.
//
// Import-free of Vue so it unit-tests without mounting the component.

import { resolveUnitCost } from '@/helpers/poCostResolver';

/**
 * Fields recomputed from the report's suggestion — protected once the row is userEdited.
 * Keep this in sync with the fields PurchaseOrderCreate.vue's updateItemTotal() derives
 * from quantity/unit-cost edits; a new editable field added there needs an entry here too,
 * or a background suggestion merge could silently clobber it.
 */
const PROTECTED_FIELDS = new Set([
  'quantity', '[average_unit_cost]', 'total', 'original_average_unit_cost', 'cost_source',
]);

export interface MergeReorderSuggestionOptions {
  supplierCost: number | null;
  exchangeRate: number | null;
  isForeignCurrency: boolean;
  supplierCurrencyCode: string;
}

/**
 * Merge one report row into one table row. Returns a NEW row object when anything
 * changed (so callers can Vue.set it back into a reactive array by index), or the
 * original `row` reference unchanged when there is nothing to merge (no matching
 * suggestion and the row wasn't 'pending').
 */
export function mergeReorderSuggestion(
  row: any,
  suggestion: any | undefined,
  opts: MergeReorderSuggestionOptions,
): any {
  if (!suggestion) {
    if (row.suggestionState === 'pending') {
      return { ...row, suggestionState: 'unavailable' };
    }
    return row;
  }

  // Protected fields are always skipped here — either the row is userEdited (keep the
  // buyer's values, already on `merged` via the spread above) or the dedicated ladder
  // recomputation below is about to overwrite them anyway. Writing them from the raw
  // suggestion first would just be discarded a moment later.
  const merged: any = { ...row };
  Object.keys(suggestion).forEach((key) => {
    if (PROTECTED_FIELDS.has(key)) { return; }
    merged[key] = suggestion[key];
  });
  // Identity stays normalized regardless of which phase produced the value —
  // report rows can return variant_id as a string.
  merged.variant_id = Number(row.variant_id);

  if (!row.userEdited) {
    const initialQuantity = Math.round(Number(suggestion['[inventory_replenishment]'])) || 0;
    const shopifyCost = Number(suggestion['[average_unit_cost]'])
      || Number(suggestion['[variant_unit_cost]'])
      || Number(suggestion.unit_cost)
      || 0;
    const { cost: effectiveCost, source: costSource } = resolveUnitCost(
      opts.supplierCost, shopifyCost, opts.exchangeRate, opts.isForeignCurrency, opts.supplierCurrencyCode,
    );
    merged.quantity = initialQuantity;
    merged.original_average_unit_cost = effectiveCost;
    merged['[average_unit_cost]'] = effectiveCost;
    merged.total = initialQuantity * effectiveCost;
    merged.cost_source = costSource;
  }

  merged.suggestionState = 'loaded';
  return merged;
}
